import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ConversationInfo } from "@/api/types";
import { ThemeToggle } from "@/components/common/ThemeToggle";
import { Wordmark } from "@/components/common/Wordmark";

type Bucket = "Today" | "Yesterday" | "This week" | "Older";

type Props = {
  conversations: ConversationInfo[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  onDelete: (id: string) => void;
  onLogout: () => void;
  email: string | null;
  mobileOpen?: boolean;
  onCloseMobile?: () => void;
};

export function ChatSidebar({
  conversations,
  activeId,
  onSelect,
  onNew,
  onDelete,
  onLogout,
  email,
  mobileOpen = false,
  onCloseMobile,
}: Props) {
  const navigate = useNavigate();
  const grouped = useMemo(() => groupByBucket(conversations), [conversations]);

  return (
    <>
      <div
        className={`sa-chat-sidebar__backdrop${mobileOpen ? " is-open" : ""}`}
        onClick={onCloseMobile}
        aria-hidden={!mobileOpen}
      />
      <aside className={`sa-chat-sidebar${mobileOpen ? " is-open" : ""}`}>
        <div className="sa-chat-sidebar__top">
          <Wordmark size={16} onClick={() => navigate("/")} />
          <button
            type="button"
            className="sa-chat-sidebar__close"
            onClick={onCloseMobile}
            aria-label="Close conversations"
          >
            X
          </button>
        </div>

        <div style={{ padding: "16px 18px" }}>
          <button
            onClick={() => {
              onNew();
              onCloseMobile?.();
            }}
            className="sa-btn sa-btn--ghost"
            style={{
              width: "100%",
              textAlign: "left",
              padding: "10px 14px",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span style={{ fontSize: 16, lineHeight: 1 }}>+</span>
            <span>New inquiry</span>
          </button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", paddingBottom: 12 }}>
          {conversations.length === 0 && (
            <div
              style={{
                padding: "12px 20px",
                fontFamily: "var(--sa-sans)",
                fontSize: 12,
                color: "var(--sa-muted-fg)",
                lineHeight: 1.5,
              }}
            >
              No conversations yet. Begin an inquiry above.
            </div>
          )}
          {(Object.keys(grouped) as Bucket[]).map((bucket) => {
            const items = grouped[bucket];
            if (!items.length) return null;

            return (
              <div key={bucket} style={{ marginTop: 10 }}>
                <div
                  className="sa-label"
                  style={{
                    padding: "8px 20px 6px",
                    fontSize: 10,
                    color: "var(--sa-muted-fg)",
                  }}
                >
                  {bucket}
                </div>
                {items.map((c) => (
                  <SidebarRow
                    key={c.id}
                    title={c.title || "Untitled inquiry"}
                    active={c.id === activeId}
                    onClick={() => {
                      onSelect(c.id);
                      onCloseMobile?.();
                    }}
                    onDelete={() => onDelete(c.id)}
                  />
                ))}
              </div>
            );
          })}
        </div>

        <div
          style={{
            borderTop: "1px solid var(--sa-border)",
            padding: "14px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            fontFamily: "var(--sa-sans)",
            fontSize: 12,
          }}
        >
          <div style={{ overflow: "hidden" }}>
            <div
              style={{
                fontWeight: 500,
                color: "var(--sa-fg)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {email || "-"}
            </div>
            <div style={{ color: "var(--sa-muted-fg)", fontSize: 11 }}>Signed in</div>
          </div>
          <button
            onClick={onLogout}
            style={{
              background: "transparent",
              border: "1px solid var(--sa-border-strong)",
              padding: "5px 10px",
              fontFamily: "var(--sa-sans)",
              fontSize: 11,
              cursor: "pointer",
              color: "var(--sa-muted-fg)",
              borderRadius: 2,
            }}
          >
            Log out
          </button>
          <ThemeToggle />
        </div>
      </aside>
    </>
  );
}

type RowProps = {
  title: string;
  active: boolean;
  onClick: () => void;
  onDelete: () => void;
};

function SidebarRow({ title, active, onClick, onDelete }: RowProps) {
  const [hover, setHover] = useState(false);
  const showDelete = hover;

  return (
    <div
      className="sa-chat-sidebar__row"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        padding: "12px 20px 12px 18px",
        cursor: "pointer",
        borderLeft: active ? "2px solid var(--sa-accent)" : "2px solid transparent",
        background: active ? "var(--sa-muted-2)" : "transparent",
      }}
    >
      <div
        style={{
          fontFamily: "var(--sa-serif)",
          fontSize: 14,
          color: "var(--sa-fg)",
          fontWeight: active ? 500 : 400,
          lineHeight: 1.4,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          paddingRight: showDelete ? 34 : 0,
        }}
      >
        {title}
      </div>
      <button
        className={`sa-chat-sidebar__delete${showDelete ? " is-visible" : ""}`}
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        aria-label="Delete conversation"
      >
        X
      </button>
    </div>
  );
}

function groupByBucket(cs: ConversationInfo[]): Record<Bucket, ConversationInfo[]> {
  const now = new Date();
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const today = startOfDay(now);
  const yesterday = today - 86_400_000;
  const weekAgo = today - 7 * 86_400_000;

  const grouped: Record<Bucket, ConversationInfo[]> = {
    Today: [],
    Yesterday: [],
    "This week": [],
    Older: [],
  };

  const sorted = [...cs].sort((a, b) => (b.updated_at ?? 0) - (a.updated_at ?? 0));
  for (const conversation of sorted) {
    const updatedAt = (conversation.updated_at ?? 0) * 1000;
    if (updatedAt >= today) grouped.Today.push(conversation);
    else if (updatedAt >= yesterday) grouped.Yesterday.push(conversation);
    else if (updatedAt >= weekAgo) grouped["This week"].push(conversation);
    else grouped.Older.push(conversation);
  }

  return grouped;
}
